import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'

export type ToastKind = 'success' | 'error' | 'warn' | 'info'

export interface ToastAction {
  label: string
  onAction: () => void | Promise<void>
}

export interface ToastOptions {
  kind?: ToastKind
  /** Adds a button to the toast, this is how undo replaces a confirm dialog. */
  action?: ToastAction
  durationMs?: number
}

interface Toast {
  id: number
  kind: ToastKind
  message: ReactNode
  action?: ToastAction
}

type Push = (message: ReactNode, kindOrOptions?: ToastKind | ToastOptions) => void

const ToastContext = createContext<Push>(() => {})

/** `const toast = useToast(); toast('Verschoben.', { action: { label: 'Rückgängig', onAction } })` */
export function useToast(): Push {
  return useContext(ToastContext)
}

let nextId = 1

/**
 * Transient confirmations. With an action attached they also replace the
 * "are you sure?" dialog: the change happens straight away and can be taken
 * back, which is both faster and safer than a prompt people click away blind.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.delete(id)
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback<Push>(
    (message, kindOrOptions) => {
      const options: ToastOptions =
        typeof kindOrOptions === 'string' ? { kind: kindOrOptions } : (kindOrOptions ?? {})
      const kind = options.kind ?? 'success'
      const id = nextId++

      setToasts((current) => [...current.slice(-3), { id, kind, message, action: options.action }])

      // An undo needs time to be noticed and used; a plain confirmation does not.
      const duration = options.durationMs ?? (options.action ? 9000 : kind === 'error' ? 8000 : 4500)
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), duration),
      )
    },
    [dismiss],
  )

  const value = useMemo(() => push, [push])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div className={`toast ${toast.kind}`} key={toast.id} role={toast.kind === 'error' ? 'alert' : 'status'}>
            <div className="body">{toast.message}</div>
            {toast.action && (
              <button
                type="button"
                className="undo"
                onClick={() => {
                  dismiss(toast.id)
                  void toast.action!.onAction()
                }}
              >
                {toast.action.label}
              </button>
            )}
            <button type="button" className="close" aria-label="Schließen" onClick={() => dismiss(toast.id)}>
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
